import React from 'react';
import { MapPin, Clock, Euro, Phone } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useTranslation } from '../hooks/useTranslation';

const DeliverySection = ({ deliveryInfo }) => {
  const { t } = useTranslation();
  
  return (
    <section id="delivery" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-black text-white text-sm font-medium tracking-wide rounded-full mb-4">
            {t('deliveryInfo')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            {t('deliveryTitle')}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('deliveryDescription')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <Card className="border-2 border-gray-200 hover:border-black transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">{t('deliveryTime')}</h3>
              <p className="text-3xl font-bold text-black mb-1">{t('deliveryMins')}</p>
              <p className="text-gray-600 text-sm">{t('averageDelivery')}</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-200 hover:border-black transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4">
                <Euro className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">{t('freeDelivery')}</h3>
              <p className="text-3xl font-bold text-black mb-1">{t('freeDeliveryAmount')}</p>
              <p className="text-gray-600 text-sm">{t('ordersOver')}</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-200 hover:border-black transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4">
                <Euro className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">{t('deliveryFee')}</h3>
              <p className="text-3xl font-bold text-black mb-1">{t('deliveryFeeAmount')}</p>
              <p className="text-gray-600 text-sm">{t('ordersEuro2035')}</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-200 hover:border-black transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">{t('pickupDiscountTitle')}</h3>
              <p className="text-3xl font-bold text-black mb-1">10%</p>
              <p className="text-gray-600 text-sm">{t('saveOnPickup')}</p>
            </CardContent>
          </Card>
        </div>

        <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-gray-200 shadow-xl max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-4xl font-bold text-black mb-3">{t('weDeliverAcrossLiepaja')}</h3>
            <p className="text-gray-600 text-lg">{t('fastReliableDelivery')}</p>
          </div>

          {/* OpenStreetMap - Interactive */}
          <div className="relative w-full h-96 rounded-2xl mb-8 overflow-hidden border-2 border-gray-300 shadow-lg bg-white">
            <iframe
              src="https://www.openstreetmap.org/export/embed.html?bbox=20.9900%2C56.5010%2C21.0100%2C56.5090&layer=mapnik&marker=56.5049875%2C20.9982351"
              className="w-full h-full brightness-105 contrast-95 saturate-50"
              style={{ border: 0 }}
              loading="lazy"
              title="Liepaja OpenStreetMap"
              allowFullScreen
            ></iframe>
            
            {/* Overlay for restaurant location */}
            <div className="absolute top-6 left-6 bg-white px-5 py-3 rounded-xl shadow-2xl border-2 border-black z-10 pointer-events-none">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-red-600 rounded-full animate-pulse"></div>
                <div>
                  <span className="font-bold text-black text-lg">Mikado Sushi</span>
                  <p className="text-sm text-gray-600">Liepu iela 9, Liepāja</p>
                </div>
              </div>
            </div>
          </div>

          {/* Address and Pickup Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="flex items-center justify-center px-6 py-4 bg-white border-2 border-black text-black rounded-xl shadow-lg hover:bg-gray-50 transition-all duration-300 transform hover:scale-105">
              <MapPin className="w-5 h-5 mr-3" />
              <span className="font-semibold">Liepu iela 9, Liepāja, Latvija</span>
            </div>
            <div className="flex items-center justify-center px-6 py-4 bg-white border-2 border-black text-black rounded-xl shadow-lg hover:bg-gray-50 transition-all duration-300 transform hover:scale-105">
              <MapPin className="w-5 h-5 mr-3" />
              <span className="font-semibold">{t('pickupAvailable')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeliverySection;